/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useState } from 'react';
import {
  Form, Row, Col, Checkbox, Spin, message, Modal, Radio,
} from 'antd';
import { I18nextProvider, useTranslation, Trans } from 'react-i18next';
import PropTypes from 'prop-types';
import { RightOutlined } from '@ant-design/icons';
import {
  FieldDescription, FormItem, Consent,
  FormInput, FormInputPassword, InviteHr,
  ConfidentialityClausule, ItemLabel, RadioButtonRegion, RadioButtonRegionText,
} from '../Forms';
import { ConfirmButton } from './buttons';
import {
  CustomCaretButton, InviteLabelForm, RegisterAccountHeader,
} from '.';
import { RegionFlag } from '..';
import { CustomerProspectService, GoogleAnalyticsService, UserService } from '../../services';
import { CustomCheckBox } from '../Containers';
import translations from '../../config/translations';
import { Field } from '../../types';
import CustomTrans from '../CustomTrans';

export const ModalWarning = Modal.warning;

const RegisterAccount = ({
  form, regionFormConfig, setStep, setUuid, traceId,
}) => {
  const { t } = useTranslation();
  const [validForm, setValidForm] = useState(false);
  const [isChecked, setChecked] = useState(false);
  const [consent] = useState(
    'CONSENT.CONSENT_TERMS_AND_CONDITIONS',
  );
  const [spin, setSpin] = useState(true);
  const [clickConfirmFailed, setClickConfirmFailed] = useState(false);
  const defaultRegion = regionFormConfig.fields.regions.defaultValue;
  const [region, setRegion] = useState(defaultRegion);

  useEffect(() => {
    setSpin(false);
  }, []);

  const onChangeConsent = (e) => {
    setChecked(e.target.checked);
    form.setFieldsValue({ [consent]: e.target.checked });
  };

  const onFinish = (values) => {
    const data = {
      region: values.region,
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phoneNumber: values.phoneNumber,
      consents: Array.of({
        code: consent,
        value: true,
      }),
    };
    setSpin(true);
    GoogleAnalyticsService.lead(traceId, 'FillAccountData');
    CustomerProspectService.createCustomerProspect(data).then((d) => {
      setUuid(d.data.uuid);
      setStep();
    }).catch((e) => {
      // eslint-disable-next-line
      console.error(e);
      message.error(t('GENERIC_ERROR'));
    })
      .finally(() => setSpin(false));
  };

  const onValid = () => {
    if (form.isFieldsTouched([Field.FIRST_NAME, Field.LAST_NAME, Field.EMAIL, Field.PHONE_NUMBER, Field.PASSWORD,
      Field.REPEAT_PASSWORD, Field.CONSENT], true)) {
      form.validateFields()
        .then(() => {
          setValidForm(true);
          setClickConfirmFailed(false);
        }).catch(() => {
          setValidForm(false);
        });
    }
  };

  const existUserModal = () => {
    Modal.warning({
      title: t('FORM_USER_ACCOUNT.MODAL_HEADER'),
      content: (
        <>
          <I18nextProvider i18n={translations.registration}>
            <Trans
              i18nKey="FORM_USER_ACCOUNT.MODAL_CONTENT"
            >
              <a href="https://track.co3.io/" target="_blank" rel="noreferrer">
                track.co3.io
              </a>
              <a href={`mailto:${t('COMMON.SUPPORT')}`}>{t('COMMON.SUPPORT')}</a>
            </Trans>
          </I18nextProvider>
        </>),
      width: '482px',
    });
  };

  return (
    <>
      <Spin spinning={spin}>
        <RegisterAccountHeader />
        <Form
          requiredMark={false}
          form={form}
          name="control-hooks"
          onFinish={onFinish}
          onChange={() => onValid()}
          onFinishFailed={() => setClickConfirmFailed(true)}
        >
          <Row style={{
            marginLeft: '-3px',
            marginRight: '-3px',
          }}
          >
            <Col span={1} />
            <Col span={22}>
              <Row>
                <FormItem
                  name={Field.REGION}
                  initialValue={defaultRegion}
                  rules={[{
                    required: true,
                    message: '',
                  }]}
                  marginBottom="-10px"
                  labelCol={{ span: 24, offset: 6 }}
                  label={
                    (
                      <ItemLabel style={{ fontWeight: 400, fontSize: '14px' }}>
                        {t('FORM_USER_ACCOUNT.REGION_SELECTION')}
                      </ItemLabel>
                    )
                  }
                  style={{ width: '100%', display: 'block' }}
                >
                  <Radio.Group
                    onChange={(e) => setRegion(e.target.value)}
                    style={{ width: '100%' }}
                  >
                    <Row justify="space-evenly">
                      {Object.values(regionFormConfig.fields.regions.values).map(({ code, disabled }) => (
                        <RadioButtonRegion value={code} disabled={disabled}>
                          <Row align="middle">
                            <Col span={11}>
                              <Row>
                                <RegionFlag region={code} />
                              </Row>
                            </Col>
                            <Col span={13}>
                              <Row>
                                <RadioButtonRegionText>
                                  {t(`FORM_USER_ACCOUNT.REGION_${code}`)}
                                </RadioButtonRegionText>
                              </Row>
                            </Col>
                          </Row>
                        </RadioButtonRegion>
                      ))}
                    </Row>
                  </Radio.Group>
                </FormItem>
              </Row>
            </Col>
            <Col span={1} />
          </Row>
          {clickConfirmFailed
            ? (
              <Row
                style={{
                  marginTop: '-5px',
                  marginBottom: '-19px',
                  marginLeft: '-3px',
                }}
              >
                <Col span={1} />
                <Col span={23}>
                  <p style={{ color: 'red' }}>{t('FORM_USER_ACCOUNT.GLOBAL_VALIDATION_MESSAGE')}</p>
                </Col>
              </Row>
            ) : (<></>)}
          <Row>
            <Col span={1} />
            <Col span={11}>
              <FormItem
                marginBottom="-10px"
                style={{
                  width: '250px',
                  marginLeft: '-3px',
                }}
                labelCol={{ span: 24 }}
                label={t('FORM_USER_ACCOUNT.FIRST_NAME')}
                name={Field.FIRST_NAME}
                rules={[
                  {
                    required: true,
                    message: '',
                  },
                  {
                    type: 'string',
                    max: 50,
                    message: t('COMMON.VALIDATION.MAX_50'),
                  },
                  {
                    type: 'string',
                    min: 3,
                    message: t('COMMON.VALIDATION.MIN_3'),
                  },
                ]}
              >
                <FormInput placeholder={t('FORM_USER_ACCOUNT.FIRST_NAME_MASK')} />
              </FormItem>
            </Col>
            <Col span={11}>
              <FormItem
                marginBottom="-10px"
                style={{
                  width: '250px',
                  marginLeft: '20px',
                }}
                labelCol={{ span: 24 }}
                label={t('FORM_USER_ACCOUNT.LAST_NAME')}
                name={Field.LAST_NAME}
                rules={[
                  {
                    required: true,
                    message: '',
                  },
                  {
                    type: 'string',
                    max: 50,
                    message: t('COMMON.VALIDATION.MAX_50'),
                  },
                  {
                    type: 'string',
                    min: 3,
                    message: t('COMMON.VALIDATION.MIN_3'),
                  },
                ]}
              >
                <FormInput placeholder={t('FORM_USER_ACCOUNT.LAST_NAME_MASK')} />
              </FormItem>
              <Col span={1} />
            </Col>
          </Row>
          <Row style={{ marginTop: '-10px' }}>
            <Col span={1} />
            <Col span={11}>
              <FormItem
                marginBottom="-10px"
                style={{
                  width: '250px',
                  marginLeft: '-3px',
                }}
                labelCol={{ span: 24 }}
                label={(
                  <InviteLabelForm
                    label={t('FORM_USER_ACCOUNT.EMAIL')}
                    description={t('FORM_USER_ACCOUNT.EMAIL_DESC')}
                  />
                )}
                name={Field.EMAIL}
                validateTrigger={['onChange', 'onBlur']}
                rules={[
                  {
                    required: true,
                    message: '',
                    validateTrigger: 'onChange',
                  },
                  {
                    required: false,
                    type: 'email',
                    message: t('FORM_USER_ACCOUNT.EMAIL_INVALID_PATTERN_MSG'),
                    validateTrigger: 'onChange',
                  },
                  {
                    async validator(rule, value) {
                      const result = await UserService.existsByEmail(value);
                      if (result) {
                        existUserModal();
                        return Promise.reject(
                          new Error(t('FORM_USER_ACCOUNT.EMAIL_EXIST_MSG')),
                        );
                      }
                      return Promise.resolve();
                    },
                    validateTrigger: 'onBlur',
                  },
                ]}
              >
                <FormInput placeholder={t('FORM_USER_ACCOUNT.EMAIL_MASK')} />
              </FormItem>
            </Col>
            <Col span={11}>
              <FormItem
                marginBottom="-10px"
                style={{
                  width: '250px',
                  marginLeft: '20px',
                }}
                labelCol={{ span: 24 }}
                label={t('FORM_USER_ACCOUNT.PHONE_NUMBER')}
                name={Field.PHONE_NUMBER}
                validateTrigger={['onChange']}
                rules={[
                  {
                    required: true,
                    message: '',
                  },
                  {
                    type: 'string',
                    max: 30,
                    message: t('COMMON.VALIDATION.MAX_30'),
                  },
                  {
                    type: 'string',
                    min: 3,
                    message: t('COMMON.VALIDATION.MIN_3'),
                  },
                ]}
              >
                <FormInput placeholder={t(`FORM_USER_ACCOUNT.PHONE_NUMBER_MASK_${region}`)} />
              </FormItem>
            </Col>
          </Row>
          <Row style={{ marginTop: '-10px' }}>
            <Col span={1} />
            <Col span={11}>
              <FormItem
                marginBottom="-10px"
                style={{
                  width: '250px',
                  marginLeft: '-3px',
                }}
                key="password"
                extra={(<FieldDescription>{t('FORM_USER_ACCOUNT.PASSWORD_EXTRA')}</FieldDescription>)}
                labelCol={{ span: 24 }}
                label={t('FORM_USER_ACCOUNT.SET_UP_THE_PASSWORD')}
                name={Field.PASSWORD}
                rules={[
                  {
                    required: true,
                    message: '',
                  },
                  {
                    type: 'string',
                    max: 50,
                    message: t('COMMON.VALIDATION.MAX_50'),
                  },
                  {
                    type: 'string',
                    min: 8,
                    message: t('COMMON.VALIDATION.MIN_8'),
                  },
                ]}
              >
                <FormInputPassword
                  placeholder={t('FORM_USER_ACCOUNT.PASSWORD_MASK')}
                />
              </FormItem>
            </Col>
            <Col span={11}>
              <FormItem
                marginBottom="-10px"
                style={{
                  width: '250px',
                  marginLeft: '20px',
                }}
                extra={<FieldDescription>{t('FORM_USER_ACCOUNT.REPEAT_PASSWORD_EXTRA')}</FieldDescription>}
                labelCol={{ span: 24 }}
                label={t('FORM_USER_ACCOUNT.REPEAT_PASSWORD')}
                name={Field.REPEAT_PASSWORD}
                rules={[
                  {
                    required: true,
                    message: '',
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (getFieldValue(Field.PASSWORD) === undefined || getFieldValue(Field.PASSWORD) === '') {
                        return Promise.resolve();
                      }
                      if (!value || getFieldValue(Field.PASSWORD) === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(t('FORM_USER_ACCOUNT.PASSWORD_DO_NOT_MATCH')),
                      );
                    },
                  }),
                ]}
              >
                <FormInputPassword
                  placeholder={t('FORM_USER_ACCOUNT.REPEAT_PASSWORD_MASK')}
                />
              </FormItem>
            </Col>
            <Col span={1} />
          </Row>
          <Row
            style={{
              marginLeft: '-3px',
            }}
          >
            <Col span={1} />
            <Col span={23} />
          </Row>
          <Row style={{ marginTop: '0px' }}>
            <Col span={1} />
            <Col
              span={22}
              style={{
                marginLeft: '-3px',
              }}
            >
              <InviteHr />
            </Col>
          </Row>
          <Row style={{ marginTop: '10px' }}>
            <Col span={1} />
            <Col span={23}>
              <Checkbox.Group>
                <Row>
                  <FormItem
                    style={{
                      marginLeft: '-3px',
                      width: 'full',
                    }}
                    key="consent"
                    name={Field.CONSENT}
                    valuePropName="checked"
                    rules={[
                      {
                        required: true,
                        transform: (value) => value || undefined,
                        type: 'boolean',
                        message: () => null,
                      },
                    ]}
                  >
                    <CustomCheckBox
                      color={clickConfirmFailed
                        && (form.getFieldValue(Field.CONSENT) === undefined
                          || form.getFieldValue(Field.CONSENT) === false)
                        ? 'red' : undefined}
                      onChange={onChangeConsent}
                      checked={isChecked}
                    >
                      <Consent>
                        <CustomTrans label={consent} />
                      </Consent>
                    </CustomCheckBox>
                  </FormItem>
                </Row>
              </Checkbox.Group>
            </Col>
          </Row>
          <Row>
            <Col span={1} />
            <Col span={23}>
              <Row style={{
                marginLeft: '-3px',
                marginTop: '-25px',
              }}
              >
                <ConfidentialityClausule>
                  <CustomCaretButton
                    header={t('CONFIDENTIALITY_CLAUSULE.HEADER')}
                    content="CONFIDENTIALITY_CLAUSULE.CONTENT"
                    region={region}
                  />
                </ConfidentialityClausule>
              </Row>
            </Col>
          </Row>
          <Row justify="center">
            <Col>
              <Form.Item
                style={{
                  marginTop: '25px',
                }}
              >
                <ConfirmButton disabled={validForm} execute={() => form.submit()} icon={<RightOutlined />} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
    </>
  );
};

export default RegisterAccount;

RegisterAccount.propTypes = {
  form: PropTypes.any.isRequired,
  regionFormConfig: PropTypes.any.isRequired,
  setStep: PropTypes.func.isRequired,
  setUuid: PropTypes.func.isRequired,
  traceId: PropTypes.number,
};

RegisterAccount.defaultProps = {
  traceId: undefined,
};
